import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  formatInrAmount as formatCurrency,
  formatIndianNumber as formatNumber,
  formatIndianPercent as formatPercent,
  formatCompactInrAxis as formatCompact,
  formatLitres,
} from "../../../utils/reportFormat";

const formatLiters = formatLitres;

const CHART_COLORS = {
  petrol: "#16A34A",
  diesel: "#2563EB",
  default: "#64748B",
};

const resolveSeriesColor = (series, index) => {
  if (!series) {
    return CHART_COLORS.default;
  }

  const name = String(series.fuel || series.label || "").toLowerCase();
  if (name.includes("diesel")) {
    return CHART_COLORS.diesel;
  }
  if (name.includes("petrol")) {
    return CHART_COLORS.petrol;
  }

  const palette = ["#5D38DB", "#D97706", "#DC2626", "#0891B2", "#7C3AED"];
  return palette[index % palette.length];
};

const FuelMixTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const amounts = payload[0]?.payload?._amounts || {};
  const quantities = payload[0]?.payload?._quantities || {};

  const rows = [...payload]
    .filter((item) => item.value != null && item.value !== "")
    .sort((a, b) => (b.value || 0) - (a.value || 0));

  return (
    <div className="owner-chart-tooltip">
      <p className="owner-chart-tooltip__title">{label}</p>
      {rows.map((item) => (
        <div className="owner-chart-tooltip__row" key={item.dataKey}>
          <span
            className="owner-chart-tooltip__dot"
            style={{ backgroundColor: item.color }}
          />
          <span className="owner-chart-tooltip__label">{item.name}</span>
          <span className="owner-chart-tooltip__value">
            {formatPercent(item.value)}
            {amounts[item.dataKey] != null ? (
              <small className="d-block text-muted">
                {formatCurrency(amounts[item.dataKey])}
                {quantities[item.dataKey] != null
                  ? ` · ${formatLiters(quantities[item.dataKey])}`
                  : ""}
              </small>
            ) : null}
          </span>
        </div>
      ))}
    </div>
  );
};

const LegendContent = ({ payload }) => {
  if (!payload?.length) {
    return null;
  }

  return (
    <div className="owner-chart-legend">
      {payload.map((entry) => (
        <span className="owner-chart-legend__item" key={entry.value}>
          <span
            className="owner-chart-legend__swatch"
            style={{ backgroundColor: entry.color }}
          />
          {entry.value}
        </span>
      ))}
    </div>
  );
};

const OwnerFuelMixChart = ({
  data = [],
  series = [],
  summary = {},
  loading = false,
}) => {
  const dominantFuel = useMemo(() => {
    const shares = summary?.shares || {};
    const entries = Object.entries(shares);
    if (!entries.length) {
      return { name: "—", pct: 0 };
    }

    const [name, pct] = entries.reduce((best, current) =>
      current[1] > best[1] ? current : best
    );

    return { name, pct };
  }, [summary]);

  if (loading) {
    return (
      <div className="owner-chart-empty">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  if (!data.length || !series.length) {
    return (
      <div className="owner-chart-empty">
        <i className="fa fa-pie-chart owner-chart-empty__icon" aria-hidden="true" />
        <p>No fuel mix data for the selected period.</p>
      </div>
    );
  }

  return (
    <div className="owner-sales-chart">
      <div className="owner-sales-kpi row g-3 mb-4">
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--green">
            <span className="owner-sales-kpi__label">Petrol share</span>
            <strong className="owner-sales-kpi__value">
              {formatPercent(summary?.shares?.Petrol)}
            </strong>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--blue">
            <span className="owner-sales-kpi__label">Diesel share</span>
            <strong className="owner-sales-kpi__value">
              {formatPercent(summary?.shares?.Diesel)}
            </strong>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--brand">
            <span className="owner-sales-kpi__label">Dominant fuel</span>
            <strong className="owner-sales-kpi__value">{dominantFuel.name}</strong>
            <small className="owner-sales-kpi__hint">
              {formatPercent(dominantFuel.pct)} of sales
            </small>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--amber">
            <span className="owner-sales-kpi__label">Total volume</span>
            <strong className="owner-sales-kpi__value">
              {formatLiters(summary?.total_quantity)}
            </strong>
          </div>
        </div>
      </div>

      <div className="owner-chart-panel">
        <ResponsiveContainer width="100%" height={360}>
          <BarChart
            data={data}
            margin={{ top: 12, right: 12, left: 4, bottom: 8 }}
          >
            <CartesianGrid stroke="#E8ECF1" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fill: "#64748b", fontSize: 12 }}
              axisLine={{ stroke: "#e2e8f0" }}
              tickLine={false}
              dy={8}
            />
            <YAxis
              domain={[0, 100]}
              tickFormatter={formatPercent}
              tick={{ fill: "#64748b", fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              width={56}
            />
            <Tooltip content={<FuelMixTooltip />} />
            <Legend content={<LegendContent />} />

            {series.map((item, index) => {
              const color = resolveSeriesColor(item, index);
              return (
                <Bar
                  key={item.key}
                  dataKey={item.key}
                  name={item.label}
                  stackId="fuel-mix"
                  fill={color}
                  radius={index === series.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                />
              );
            })}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default OwnerFuelMixChart;
